import React, { useEffect, useState } from 'react';
import { companyInfo } from '../data/mock';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section className="relative h-screen overflow-hidden bg-[#020617]">
      {/* Ultra Smooth Animated Background with Enhanced Parallax */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-out animate-zoom-pulse-smooth"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1727949193251-c0da4c1c3a5b?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundPosition: "center 40%",
          transform: `translateY(${scrollY * 0.4}px) translateX(${(mousePosition.x - window.innerWidth / 2) * 0.03}px) scale(1.15)`,
        }}
      />

      {/* Multi-Layer Gradient Overlays with Fluid Motion */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/20 via-transparent to-[#020617]/90 animate-gradient-flow" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/15 to-[#D4AF37]/0 animate-light-sweep-fluid" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/40 via-transparent to-transparent animate-gradient-breathe" />
      
      {/* Multiple Animated Light Beams - More Frequent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[15%] w-[2px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/40 to-transparent animate-beam-flow-1" />
        <div className="absolute top-0 left-[30%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/25 to-transparent animate-beam-flow-2" />
        <div className="absolute top-0 left-[45%] w-[2px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent animate-beam-flow-3" />
        <div className="absolute top-0 right-[35%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/20 to-transparent animate-beam-flow-4" />
        <div className="absolute top-0 right-[20%] w-[2px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/35 to-transparent animate-beam-flow-5" />
        <div className="absolute top-0 right-[10%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/30 to-transparent animate-beam-flow-6" />
      </div>

      {/* Massive Particle System - 50+ Particles with Smooth Motion */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Extra Large Particles - Slow, Smooth Float */}
        <div className="absolute top-[15%] right-[20%] w-4 h-4 bg-[#D4AF37]/70 rounded-full animate-float-xl-1 blur-[3px]" />
        <div className="absolute top-[30%] left-[25%] w-3.5 h-3.5 bg-white/60 rounded-full animate-float-xl-2 blur-[2px]" />
        <div className="absolute bottom-[25%] right-[30%] w-3 h-3 bg-[#D4AF37]/60 rounded-full animate-float-xl-3 blur-[3px]" />
        <div className="absolute top-[60%] left-[15%] w-4 h-4 bg-white/50 rounded-full animate-float-xl-4 blur-[2px]" />
        
        {/* Large Particles - Graceful Movement */}
        <div className="absolute top-[20%] right-[15%] w-3 h-3 bg-[#D4AF37]/65 rounded-full animate-float-large-smooth-1 blur-[2px]" />
        <div className="absolute top-[35%] left-[20%] w-2.5 h-2.5 bg-white/55 rounded-full animate-float-large-smooth-2 blur-[1px]" />
        <div className="absolute bottom-[30%] right-[25%] w-3 h-3 bg-[#D4AF37]/55 rounded-full animate-float-large-smooth-3 blur-[2px]" />
        <div className="absolute top-[50%] right-[40%] w-2.5 h-2.5 bg-white/60 rounded-full animate-float-large-smooth-4 blur-[1px]" />
        <div className="absolute bottom-[40%] left-[35%] w-3 h-3 bg-[#D4AF37]/60 rounded-full animate-float-large-smooth-5 blur-[2px]" />
        <div className="absolute top-[70%] right-[18%] w-2.5 h-2.5 bg-white/50 rounded-full animate-float-large-smooth-6 blur-[1px]" />
        
        {/* Medium Particles - Fluid Motion */}
        <div className="absolute top-[18%] left-[18%] w-2 h-2 bg-white/45 rounded-full animate-float-medium-fluid-1" />
        <div className="absolute top-[38%] right-[22%] w-2 h-2 bg-[#D4AF37]/55 rounded-full animate-float-medium-fluid-2" />
        <div className="absolute bottom-[22%] left-[28%] w-1.5 h-1.5 bg-white/65 rounded-full animate-float-medium-fluid-3" />
        <div className="absolute top-[55%] right-[35%] w-2 h-2 bg-[#D4AF37]/45 rounded-full animate-float-medium-fluid-4" />
        <div className="absolute top-[45%] left-[40%] w-1.5 h-1.5 bg-white/50 rounded-full animate-float-medium-fluid-5" />
        <div className="absolute bottom-[35%] right-[45%] w-2 h-2 bg-[#D4AF37]/50 rounded-full animate-float-medium-fluid-6" />
        <div className="absolute top-[65%] left-[22%] w-1.5 h-1.5 bg-white/55 rounded-full animate-float-medium-fluid-7" />
        <div className="absolute bottom-[50%] right-[28%] w-2 h-2 bg-[#D4AF37]/60 rounded-full animate-float-medium-fluid-8" />
        
        {/* Small Particles - Quick, Smooth Dance */}
        <div className="absolute top-[12%] right-[12%] w-1 h-1 bg-white/35 rounded-full animate-float-small-dance-1" />
        <div className="absolute top-[28%] left-[32%] w-1 h-1 bg-[#D4AF37]/55 rounded-full animate-float-small-dance-2" />
        <div className="absolute top-[42%] right-[48%] w-0.5 h-0.5 bg-white/45 rounded-full animate-float-small-dance-3" />
        <div className="absolute bottom-[18%] right-[15%] w-1 h-1 bg-[#D4AF37]/65 rounded-full animate-float-small-dance-4" />
        <div className="absolute top-[58%] left-[38%] w-0.5 h-0.5 bg-white/50 rounded-full animate-float-small-dance-5" />
        <div className="absolute bottom-[32%] right-[38%] w-1 h-1 bg-[#D4AF37]/45 rounded-full animate-float-small-dance-6" />
        <div className="absolute top-[72%] left-[28%] w-0.5 h-0.5 bg-white/40 rounded-full animate-float-small-dance-7" />
        <div className="absolute bottom-[45%] right-[52%] w-1 h-1 bg-[#D4AF37]/50 rounded-full animate-float-small-dance-8" />
        <div className="absolute top-[25%] left-[45%] w-0.5 h-0.5 bg-white/55 rounded-full animate-float-small-dance-9" />
        <div className="absolute bottom-[55%] right-[22%] w-1 h-1 bg-[#D4AF37]/40 rounded-full animate-float-small-dance-10" />
        
        {/* Micro Particles - Atmospheric */}
        <div className="absolute top-[10%] right-[30%] w-0.5 h-0.5 bg-white/30 rounded-full animate-float-micro-1" />
        <div className="absolute top-[22%] left-[48%] w-0.5 h-0.5 bg-[#D4AF37]/40 rounded-full animate-float-micro-2" />
        <div className="absolute top-[48%] right-[42%] w-0.5 h-0.5 bg-white/35 rounded-full animate-float-micro-3" />
        <div className="absolute bottom-[28%] left-[42%] w-0.5 h-0.5 bg-[#D4AF37]/45 rounded-full animate-float-micro-4" />
        <div className="absolute top-[62%] right-[32%] w-0.5 h-0.5 bg-white/40 rounded-full animate-float-micro-5" />
        <div className="absolute bottom-[42%] left-[48%] w-0.5 h-0.5 bg-[#D4AF37]/35 rounded-full animate-float-micro-6" />
        <div className="absolute top-[78%] right-[48%] w-0.5 h-0.5 bg-white/30 rounded-full animate-float-micro-7" />
        <div className="absolute bottom-[58%] left-[52%] w-0.5 h-0.5 bg-[#D4AF37]/40 rounded-full animate-float-micro-8" />
      </div>

      {/* Large Pulsing Light Orbs - Soft and Organic */}
      <div className="absolute top-[25%] left-[20%] w-48 h-48 bg-[#D4AF37]/12 rounded-full blur-3xl animate-pulse-organic-1" />
      <div className="absolute bottom-[20%] right-[18%] w-56 h-56 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse-organic-2" />
      <div className="absolute top-[50%] right-[50%] w-40 h-40 bg-white/8 rounded-full blur-3xl animate-pulse-organic-3" />

      {/* Grow Rich - Smooth Breathing Effect */}
      <div className="absolute top-24 left-0 right-0 z-10 text-center animate-dramatic-reveal-smooth">
        <h2 className="font-serif text-[6rem] md:text-[10rem] lg:text-[14rem] text-white/14 font-light tracking-[-0.02em] leading-none whitespace-nowrap select-none animate-breathe-water">
          Grow Rich
        </h2>
      </div>

      {/* Main Content */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-8 md:px-16 lg:px-32 pb-12">
          
          {/* Company Info */}
          <div className="text-center mb-12 animate-slide-up-water animation-delay-300">
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-5 tracking-tight leading-[1.1] font-normal animate-text-glow-flow">
              Rosevilla Group
            </h1>
            
            <p className="text-xl md:text-2xl lg:text-3xl text-[#D4AF37] font-light tracking-[0.05em] mb-8 animate-glow-wave" style={{ fontWeight: 300 }}>
              {companyInfo.tagline}
            </p>

            <div className="flex items-center justify-center gap-6 mb-10 animate-line-expand-smooth animation-delay-600">
              <div className="h-[1px] w-20 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-[#D4AF37]/70 animate-shimmer-flow" />
              <span className="text-[#D4AF37]/90 text-[10px] uppercase tracking-[0.35em] font-light animate-text-pulse-soft">
                Premier Investment Holdings
              </span>
              <div className="h-[1px] w-20 bg-gradient-to-l from-transparent via-[#D4AF37]/70 to-[#D4AF37]/70 animate-shimmer-flow-reverse" />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 animate-slide-up-water animation-delay-900">
            
            {/* Stats with Magnetic Hover */}
            <div className="flex gap-16">
              <div className="text-center group cursor-default">
                <div className="text-3xl lg:text-4xl font-serif text-white font-light mb-2 transition-all duration-1000 group-hover:text-[#D4AF37] group-hover:scale-125 group-hover:drop-shadow-[0_0_25px_rgba(212,175,55,0.7)] animate-stat-float">$4B+</div>
                <div className="text-[#94A3B8]/80 text-[9px] uppercase tracking-[0.15em] font-light">Collectively Deployed</div>
              </div>
              <div className="text-center group cursor-default">
                <div className="text-3xl lg:text-4xl font-serif text-white font-light mb-2 transition-all duration-1000 group-hover:text-[#D4AF37] group-hover:scale-125 group-hover:drop-shadow-[0_0_25px_rgba(212,175,55,0.7)] animate-stat-float">200+</div>
                <div className="text-[#94A3B8]/80 text-[9px] uppercase tracking-[0.15em] font-light">Years Combined Experience</div>
              </div>
              <div className="text-center group cursor-default">
                <div className="text-3xl lg:text-4xl font-serif text-white font-light mb-2 transition-all duration-1000 group-hover:text-[#D4AF37] group-hover:scale-125 group-hover:drop-shadow-[0_0_25px_rgba(212,175,55,0.7)] animate-stat-float">3</div>
                <div className="text-[#94A3B8]/80 text-[9px] uppercase tracking-[0.15em] font-light">Continents</div>
              </div>
            </div>

            {/* CTAs with Liquid Effects */}
            <div className="flex gap-4">
              <Button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="group relative bg-[#D4AF37] hover:bg-[#C9A350] text-[#020617] px-10 py-5 font-serif text-sm tracking-[0.05em] transition-all duration-1000 ease-out hover:scale-110 hover:shadow-2xl hover:shadow-[#D4AF37]/50 overflow-hidden animate-pulse-liquid"
              >
                <span className="relative z-10 flex items-center">
                  Explore Opportunities
                  <ArrowRight className="ml-3 w-4 h-4 transition-transform duration-1000 ease-out group-hover:translate-x-3" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
              </Button>
              <Button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="group bg-transparent border border-[#D4AF37]/80 text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] px-10 py-5 font-serif text-sm tracking-[0.05em] transition-all duration-1000 ease-out hover:scale-110 backdrop-blur-sm animate-border-flow"
              >
                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Ultra-Smooth CSS Animations - Water-Like Movement */}
      <style jsx>{`
        @keyframes zoom-pulse-smooth {
          0%, 100% { transform: scale(1.12); }
          50% { transform: scale(1.18); }
        }

        @keyframes gradient-flow {
          0%, 100% { opacity: 0.90; }
          50% { opacity: 1; }
        }

        @keyframes gradient-breathe {
          0%, 100% { opacity: 0.40; }
          50% { opacity: 0.60; }
        }

        @keyframes light-sweep-fluid {
          0% { transform: translateX(-100%) rotate(15deg); opacity: 0; }
          30% { opacity: 0.4; }
          70% { opacity: 0.4; }
          100% { transform: translateX(200%) rotate(15deg); opacity: 0; }
        }

        @keyframes beam-flow-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.4; transform: translateY(100%); }
        }

        @keyframes beam-flow-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.25; transform: translateY(-100%); }
        }

        @keyframes beam-flow-3 {
          0%, 100% { opacity: 0; transform: translateY(-50%); }
          50% { opacity: 0.3; transform: translateY(150%); }
        }

        @keyframes beam-flow-4 {
          0%, 100% { opacity: 0; transform: translateY(150%); }
          50% { opacity: 0.2; transform: translateY(-50%); }
        }

        @keyframes beam-flow-5 {
          0%, 100% { opacity: 0; transform: translateY(-80%); }
          50% { opacity: 0.35; transform: translateY(120%); }
        }

        @keyframes beam-flow-6 {
          0%, 100% { opacity: 0; transform: translateY(120%); }
          50% { opacity: 0.3; transform: translateY(-80%); }
        }

        /* Extra Large Particles - Slow, Graceful */
        @keyframes float-xl-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.7; }
          25% { transform: translate(80px, -100px) scale(1.2); opacity: 0.85; }
          50% { transform: translate(120px, 40px) scale(1.4); opacity: 0.95; }
          75% { transform: translate(40px, 120px) scale(0.9); opacity: 0.75; }
        }

        @keyframes float-xl-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.6; }
          50% { transform: translate(-100px, 130px) rotate(180deg); opacity: 0.85; }
        }

        @keyframes float-xl-3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          33% { transform: translate(100px, -90px) scale(1.3); opacity: 0.8; }
          66% { transform: translate(-60px, 80px) scale(1.1); opacity: 0.9; }
        }

        @keyframes float-xl-4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0.5; }
          50% { transform: translate(90px, -110px) rotate(270deg) scale(1.3); opacity: 0.8; }
        }

        /* Large Particles - Smooth Flow */
        @keyframes float-large-smooth-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.65; }
          33% { transform: translate(70px, -90px) scale(1.35); opacity: 0.85; }
          66% { transform: translate(-50px, 70px) scale(0.85); opacity: 0.6; }
        }

        @keyframes float-large-smooth-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.55; }
          50% { transform: translate(-80px, 100px) rotate(200deg); opacity: 0.75; }
        }

        @keyframes float-large-smooth-3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(90px, -80px) scale(1.5); opacity: 0.85; }
        }

        @keyframes float-large-smooth-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          33% { transform: translate(60px, 80px); opacity: 0.8; }
          66% { transform: translate(-70px, -50px); opacity: 0.7; }
        }

        @keyframes float-large-smooth-5 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0.6; }
          50% { transform: translate(-85px, -95px) rotate(180deg) scale(1.3); opacity: 0.8; }
        }

        @keyframes float-large-smooth-6 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(75px, 85px) scale(1.25); opacity: 0.75; }
        }

        /* Medium Particles - Fluid Dance */
        @keyframes float-medium-fluid-1 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(50px, -60px); opacity: 0.85; }
        }

        @keyframes float-medium-fluid-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.55; }
          50% { transform: translate(-60px, 70px); opacity: 0.95; }
        }

        @keyframes float-medium-fluid-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.65; }
          50% { transform: translate(40px, -50px); opacity: 0.95; }
        }

        @keyframes float-medium-fluid-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(-50px, -60px); opacity: 0.75; }
        }

        @keyframes float-medium-fluid-5 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
          50% { transform: translate(55px, 65px) rotate(180deg); opacity: 0.85; }
        }

        @keyframes float-medium-fluid-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-45px, 55px); opacity: 0.8; }
        }

        @keyframes float-medium-fluid-7 {
          0%, 100% { transform: translate(0, 0); opacity: 0.55; }
          50% { transform: translate(48px, -58px); opacity: 0.9; }
        }

        @keyframes float-medium-fluid-8 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(-52px, -62px) scale(1.2); opacity: 0.85; }
        }

        /* Small Particles - Quick Dance */
        @keyframes float-small-dance-1 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(30px, -40px); opacity: 0.7; }
        }

        @keyframes float-small-dance-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.55; }
          50% { transform: translate(-35px, 45px); opacity: 0.85; }
        }

        @keyframes float-small-dance-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(25px, -30px); opacity: 0.75; }
        }

        @keyframes float-small-dance-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.65; }
          50% { transform: translate(-28px, -38px); opacity: 0.9; }
        }

        @keyframes float-small-dance-5 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
          50% { transform: translate(32px, 42px) rotate(180deg); opacity: 0.8; }
        }

        @keyframes float-small-dance-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(-30px, 40px); opacity: 0.75; }
        }

        @keyframes float-small-dance-7 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(28px, -36px); opacity: 0.7; }
        }

        @keyframes float-small-dance-8 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-32px, -42px); opacity: 0.8; }
        }

        @keyframes float-small-dance-9 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(26px, 36px) scale(1.3); opacity: 0.85; }
        }

        @keyframes float-small-dance-10 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(-29px, 39px); opacity: 0.7; }
        }

        /* Micro Particles - Atmospheric */
        @keyframes float-micro-1 {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          50% { transform: translate(20px, -25px); opacity: 0.6; }
        }

        @keyframes float-micro-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(-22px, 28px); opacity: 0.7; }
        }

        @keyframes float-micro-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(18px, -22px); opacity: 0.65; }
        }

        @keyframes float-micro-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(-24px, -30px); opacity: 0.75; }
        }

        @keyframes float-micro-5 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(21px, 26px); opacity: 0.7; }
        }

        @keyframes float-micro-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(-19px, 24px); opacity: 0.65; }
        }

        @keyframes float-micro-7 {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          50% { transform: translate(23px, -28px); opacity: 0.6; }
        }

        @keyframes float-micro-8 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(-20px, -26px); opacity: 0.7; }
        }

        /* Organic Pulsing Lights */
        @keyframes pulse-organic-1 {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.3); }
        }

        @keyframes pulse-organic-2 {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.4); }
        }

        @keyframes pulse-organic-3 {
          0%, 100% { opacity: 0.06; transform: scale(1); }
          50% { opacity: 0.12; transform: scale(1.35); }
        }

        /* Water-Like Breathing */
        @keyframes breathe-water {
          0%, 100% { opacity: 0.14; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.03); }
        }

        @keyframes text-glow-flow {
          0%, 100% { 
            text-shadow: 0 0 25px rgba(255, 255, 255, 0.18), 0 0 50px rgba(255, 255, 255, 0.12);
          }
          50% { 
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.28), 0 0 70px rgba(255, 255, 255, 0.18), 0 0 90px rgba(255, 255, 255, 0.08);
          }
        }

        @keyframes glow-wave {
          0%, 100% { 
            text-shadow: 0 0 25px rgba(212, 175, 55, 0.35);
            filter: brightness(1);
          }
          50% { 
            text-shadow: 0 0 50px rgba(212, 175, 55, 0.55), 0 0 75px rgba(212, 175, 55, 0.25);
            filter: brightness(1.25);
          }
        }

        @keyframes dramatic-reveal-smooth {
          0% { opacity: 0; transform: translateY(-60px); filter: blur(25px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @keyframes slide-up-water {
          0% { opacity: 0; transform: translateY(70px); filter: blur(12px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @keyframes line-expand-smooth {
          0% { opacity: 0; transform: scaleX(0); }
          100% { opacity: 1; transform: scaleX(1); }
        }

        @keyframes shimmer-flow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }

        @keyframes shimmer-flow-reverse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes text-pulse-soft {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 1; }
        }

        @keyframes bounce-in-smooth {
          0% { opacity: 0; transform: scale(0.5); }
          50% { transform: scale(1.08); }
          75% { transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes stat-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes pop-in-smooth {
          0% { opacity: 0; transform: scale(0.85); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes pulse-liquid {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.5); }
          50% { box-shadow: 0 0 30px 15px rgba(212, 175, 55, 0); }
        }

        @keyframes border-flow {
          0%, 100% { border-color: rgba(212, 175, 55, 0.8); }
          50% { border-color: rgba(212, 175, 55, 1); }
        }

        /* Apply Animations */
        .animate-zoom-pulse-smooth { animation: zoom-pulse-smooth 25s ease-in-out infinite; }
        .animate-gradient-flow { animation: gradient-flow 10s ease-in-out infinite; }
        .animate-gradient-breathe { animation: gradient-breathe 12s ease-in-out infinite; }
        .animate-light-sweep-fluid { animation: light-sweep-fluid 15s ease-in-out infinite; }
        .animate-beam-flow-1 { animation: beam-flow-1 12s ease-in-out infinite; }
        .animate-beam-flow-2 { animation: beam-flow-2 14s ease-in-out infinite; }
        .animate-beam-flow-3 { animation: beam-flow-3 16s ease-in-out infinite; }
        .animate-beam-flow-4 { animation: beam-flow-4 13s ease-in-out infinite; }
        .animate-beam-flow-5 { animation: beam-flow-5 15s ease-in-out infinite; }
        .animate-beam-flow-6 { animation: beam-flow-6 17s ease-in-out infinite; }
        
        .animate-float-xl-1 { animation: float-xl-1 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-xl-2 { animation: float-xl-2 25s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-xl-3 { animation: float-xl-3 23s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-xl-4 { animation: float-xl-4 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        
        .animate-float-large-smooth-1 { animation: float-large-smooth-1 18s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-large-smooth-2 { animation: float-large-smooth-2 20s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-large-smooth-3 { animation: float-large-smooth-3 19s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-large-smooth-4 { animation: float-large-smooth-4 21s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-large-smooth-5 { animation: float-large-smooth-5 20s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-large-smooth-6 { animation: float-large-smooth-6 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        
        .animate-float-medium-fluid-1 { animation: float-medium-fluid-1 15s ease-in-out infinite; }
        .animate-float-medium-fluid-2 { animation: float-medium-fluid-2 16s ease-in-out infinite; }
        .animate-float-medium-fluid-3 { animation: float-medium-fluid-3 14s ease-in-out infinite; }
        .animate-float-medium-fluid-4 { animation: float-medium-fluid-4 17s ease-in-out infinite; }
        .animate-float-medium-fluid-5 { animation: float-medium-fluid-5 16s ease-in-out infinite; }
        .animate-float-medium-fluid-6 { animation: float-medium-fluid-6 15s ease-in-out infinite; }
        .animate-float-medium-fluid-7 { animation: float-medium-fluid-7 18s ease-in-out infinite; }
        .animate-float-medium-fluid-8 { animation: float-medium-fluid-8 17s ease-in-out infinite; }
        
        .animate-float-small-dance-1 { animation: float-small-dance-1 12s ease-in-out infinite; }
        .animate-float-small-dance-2 { animation: float-small-dance-2 13s ease-in-out infinite; }
        .animate-float-small-dance-3 { animation: float-small-dance-3 11s ease-in-out infinite; }
        .animate-float-small-dance-4 { animation: float-small-dance-4 14s ease-in-out infinite; }
        .animate-float-small-dance-5 { animation: float-small-dance-5 13s ease-in-out infinite; }
        .animate-float-small-dance-6 { animation: float-small-dance-6 12s ease-in-out infinite; }
        .animate-float-small-dance-7 { animation: float-small-dance-7 15s ease-in-out infinite; }
        .animate-float-small-dance-8 { animation: float-small-dance-8 14s ease-in-out infinite; }
        .animate-float-small-dance-9 { animation: float-small-dance-9 13s ease-in-out infinite; }
        .animate-float-small-dance-10 { animation: float-small-dance-10 16s ease-in-out infinite; }
        
        .animate-float-micro-1 { animation: float-micro-1 10s ease-in-out infinite; }
        .animate-float-micro-2 { animation: float-micro-2 11s ease-in-out infinite; }
        .animate-float-micro-3 { animation: float-micro-3 9s ease-in-out infinite; }
        .animate-float-micro-4 { animation: float-micro-4 12s ease-in-out infinite; }
        .animate-float-micro-5 { animation: float-micro-5 10s ease-in-out infinite; }
        .animate-float-micro-6 { animation: float-micro-6 11s ease-in-out infinite; }
        .animate-float-micro-7 { animation: float-micro-7 13s ease-in-out infinite; }
        .animate-float-micro-8 { animation: float-micro-8 12s ease-in-out infinite; }
        
        .animate-pulse-organic-1 { animation: pulse-organic-1 8s ease-in-out infinite; }
        .animate-pulse-organic-2 { animation: pulse-organic-2 10s ease-in-out infinite; }
        .animate-pulse-organic-3 { animation: pulse-organic-3 9s ease-in-out infinite; }
        
        .animate-breathe-water { animation: breathe-water 8s ease-in-out infinite; }
        .animate-text-glow-flow { animation: text-glow-flow 4s ease-in-out infinite; }
        .animate-glow-wave { animation: glow-wave 5s ease-in-out infinite; }
        .animate-dramatic-reveal-smooth { animation: dramatic-reveal-smooth 2.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slide-up-water { animation: slide-up-water 2s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-line-expand-smooth { animation: line-expand-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-shimmer-flow { animation: shimmer-flow 4s ease-in-out infinite; }
        .animate-shimmer-flow-reverse { animation: shimmer-flow-reverse 4s ease-in-out infinite; }
        .animate-text-pulse-soft { animation: text-pulse-soft 3s ease-in-out infinite; }
        .animate-bounce-in-smooth { animation: bounce-in-smooth 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; }
        .animate-stat-float { animation: stat-float 6s ease-in-out infinite; }
        .animate-pop-in-smooth { animation: pop-in-smooth 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; }
        .animate-pulse-liquid { animation: pulse-liquid 3s ease-in-out infinite; }
        .animate-border-flow { animation: border-flow 3s ease-in-out infinite; }

        .animation-delay-200 { animation-delay: 0.2s; opacity: 0; }
        .animation-delay-300 { animation-delay: 0.3s; opacity: 0; }
        .animation-delay-400 { animation-delay: 0.4s; opacity: 0; }
        .animation-delay-600 { animation-delay: 0.6s; opacity: 0; }
        .animation-delay-900 { animation-delay: 0.9s; opacity: 0; }
        .animation-delay-1100 { animation-delay: 1.1s; opacity: 0; }
        .animation-delay-1300 { animation-delay: 1.3s; opacity: 0; }
        .animation-delay-1500 { animation-delay: 1.5s; opacity: 0; }
        .animation-delay-1700 { animation-delay: 1.7s; opacity: 0; }

        .drop-shadow-glow {
          filter: drop-shadow(0 0 25px rgba(212, 175, 55, 0.7));
        }
      `}</style>
    </section>
  );
};

export default Hero;
